import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Button,
  useToast,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const AdminPlanSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [upgradeIntents, setUpgradeIntents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editedConfigs, setEditedConfigs] = useState({});
  const [switchingUser, setSwitchingUser] = useState(null);
  const [hideAdminUsers, setHideAdminUsers] = useState(true);
  const [pricing, setPricing] = useState({
    proMonthlyPrice: 19.99,
    proOriginalPrice: 12.00,
    proDiscountPercent: 42
  });
  const [isSavingPricing, setIsSavingPricing] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [configsRes, intentsRes, pricingRes] = await Promise.all([
        fetchWithAuth("/api/subscription/admin/configs"),
        fetchWithAuth("/api/subscription/admin/upgrade-intents"),
        fetch("/api/subscription/pricing"),
      ]);
      
      if (configsRes.ok) {
        const data = await configsRes.json();
        setConfigs(data);
        const initial = {};
        data.forEach((c) => {
          initial[c.plan_type] = { ...c };
        });
        setEditedConfigs(initial);
      }
      
      if (intentsRes.ok) {
        setUpgradeIntents(await intentsRes.json());
      }
      
      if (pricingRes.ok) {
        setPricing(await pricingRes.json());
      }
    } catch (error) {
      console.error("Error fetching plan settings:", error);
      toast({
        title: "Error loading settings",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (planType, field, value) => {
    setEditedConfigs((prev) => ({
      ...prev,
      [planType]: {
        ...prev[planType],
        [field]: value === "" ? null : parseInt(value, 10) || null,
      },
    }));
  };

  const handleSave = async (planType) => {
    setIsSaving(true);
    try {
      const config = editedConfigs[planType];
      const response = await fetchWithAuth(
        `/api/subscription/admin/configs/${planType}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            maxPlans: config.max_plans,
            maxStrategyRegens: config.max_strategy_regens,
            maxCompetitorRegens: config.max_competitor_regens,
            maxDocsGenerations: config.max_docs_generations,
            maxActionPlanGenerations: config.max_action_plan_generations,
            maxDailyChats: config.max_daily_chats,
            maxPulseGenerations: config.max_pulse_generations,
            maxLearningsEvaluations: config.max_learnings_evaluations,
            maxAgentTasks: config.max_agent_tasks,
            monthlyCreditsAllowance: config.monthly_credits_allowance,
            pulseRefreshLearnings: config.pulse_refresh_learnings,
          }),
        }
      );
      
      if (response.ok) {
        toast({
          title: "Settings saved",
          status: "success",
          duration: 2000,
        });
        fetchData();
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error saving settings",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSavePricing = async () => {
    setIsSavingPricing(true);
    try {
      const response = await fetchWithAuth("/api/subscription/pricing", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pricing),
      });
      
      if (response.ok) {
        toast({
          title: "Pricing updated",
          description: `Pro plan: $${pricing.proMonthlyPrice} (${pricing.proDiscountPercent}% off $${pricing.proOriginalPrice})`,
          status: "success",
          duration: 3000,
        });
      } else {
        throw new Error("Failed to save pricing");
      }
    } catch (error) {
      console.error("Error saving pricing:", error);
      toast({
        title: "Error saving pricing",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSavingPricing(false);
    }
  };

  const handleSwitchToPro = async (userId, userName) => {
    setSwitchingUser(userId);
    try {
      const response = await fetchWithAuth(
        `/api/subscription/admin/switch-to-pro/${userId}`,
        { method: "POST" }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: "User upgraded to Pro",
          description: data.message,
          status: "success",
          duration: 4000,
        });
        fetchData();
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to switch user");
      }
    } catch (error) {
      console.error("Error switching user to Pro:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to switch user to Pro",
        status: "error",
        duration: 3000,
      });
    } finally {
      setSwitchingUser(null);
    }
  };

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
        <Text mt={4}>Loading plan settings...</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Heading size="lg">Plan Settings</Heading>
        <Text color="gray.600">
          Configure limits for each plan tier. Leave empty for unlimited.
        </Text>

        <Tabs colorScheme="brand">
          <TabList>
            <Tab>Plan Limits</Tab>
            <Tab>Pricing</Tab>
            <Tab>
              Upgrade Funnel
              {(() => {
                const filteredIntents = hideAdminUsers 
                  ? upgradeIntents.filter(i => !i.is_admin) 
                  : upgradeIntents;
                const count = new Set(filteredIntents.map(i => i.user_id)).size;
                return count > 0 ? (
                  <Badge ml={2} colorScheme="green">{count}</Badge>
                ) : null;
              })()}
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                {configs.map((config) => (
                  <Card key={config.plan_type}>
                    <CardHeader pb={2}>
                      <HStack justify="space-between">
                        <HStack>
                          <Heading size="md" textTransform="capitalize">
                            {config.plan_type} Plan
                          </Heading>
                          {config.plan_type === "pro" && (
                            <Badge colorScheme="brand">Premium</Badge>
                          )}
                        </HStack>
                        <Button
                          size="sm"
                          colorScheme="brand"
                          onClick={() => handleSave(config.plan_type)}
                          isLoading={isSaving}
                        >
                          Save Changes
                        </Button>
                      </HStack>
                    </CardHeader>
                    <CardBody>
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>Limit</Th>
                            <Th>Value</Th>
                            <Th>Description</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td fontWeight="medium">Max Plans</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="Unlimited"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]?.max_plans ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "max_plans",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              Total plans a user can create
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Strategy Regens</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="Unlimited"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]
                                    ?.max_strategy_regens ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "max_strategy_regens",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              Strategy regenerations per plan
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Competitor Regens</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="Unlimited"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]
                                    ?.max_competitor_regens ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "max_competitor_regens",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              Competitor analysis regenerations per plan
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Doc Generations</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="Unlimited"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]
                                    ?.max_docs_generations ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "max_docs_generations",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              Document generations per plan (PRD, SOW, etc)
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Action Plan Gens</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="Unlimited"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]
                                    ?.max_action_plan_generations ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "max_action_plan_generations",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              Action plan generations per plan
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Daily Chats</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="Unlimited"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]?.max_daily_chats ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "max_daily_chats",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              Chat messages per day
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Pulse Generations</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="Unlimited"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]?.max_pulse_generations ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "max_pulse_generations",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              Pulse regenerations per plan (leave empty for unlimited)
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Pulse Auto-Refresh</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="3"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]?.pulse_refresh_learnings ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "pulse_refresh_learnings",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              New learnings (a Brief edit counts as one) before Pulse refreshes on its own when opened. A resolved next step or blocker, or a new strategy or competitor analysis, refreshes it right away. Refresh always works by hand. Leave empty for 3.
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Learnings Evaluations</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="Unlimited"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]?.max_learnings_evaluations ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "max_learnings_evaluations",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              AI evaluations per plan (leave empty for unlimited)
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Agent Tasks</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="Unlimited"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]?.max_agent_tasks ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "max_agent_tasks",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              Agent-created tasks per plan (leave empty for unlimited)
                            </Td>
                          </Tr>
                          <Tr>
                            <Td fontWeight="medium">Monthly Credits</Td>
                            <Td>
                              <Input
                                size="sm"
                                type="number"
                                placeholder="30"
                                w="100px"
                                value={
                                  editedConfigs[config.plan_type]?.monthly_credits_allowance ?? ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    config.plan_type,
                                    "monthly_credits_allowance",
                                    e.target.value
                                  )
                                }
                              />
                            </Td>
                            <Td color="gray.500" fontSize="sm">
                              Credits granted per month on this tier. New signups and upgrades get it immediately; existing users on this tier get it at their next monthly reset, today's balance is untouched.
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            </TabPanel>

            <TabPanel px={0}>
              <Card>
                <CardHeader pb={2}>
                  <HStack justify="space-between">
                    <Heading size="md">Pro Plan Pricing</Heading>
                    <Button
                      size="sm"
                      colorScheme="brand"
                      onClick={handleSavePricing}
                      isLoading={isSavingPricing}
                    >
                      Save Pricing
                    </Button>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={6}>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Original Price ($)</Text>
                        <Input
                          type="number"
                          step="0.01"
                          w="120px"
                          value={pricing.proOriginalPrice}
                          onChange={(e) => setPricing(prev => ({
                            ...prev,
                            proOriginalPrice: parseFloat(e.target.value) || 0
                          }))}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>Shown crossed out</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Discount (%)</Text>
                        <Input
                          type="number"
                          w="100px"
                          value={pricing.proDiscountPercent}
                          onChange={(e) => setPricing(prev => ({
                            ...prev,
                            proDiscountPercent: parseInt(e.target.value) || 0
                          }))}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>Launch discount</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Final Price ($)</Text>
                        <Input
                          type="number"
                          step="0.01"
                          w="120px"
                          value={pricing.proMonthlyPrice}
                          onChange={(e) => setPricing(prev => ({
                            ...prev,
                            proMonthlyPrice: parseFloat(e.target.value) || 0
                          }))}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>What users pay</Text>
                      </Box>
                    </HStack>
                    <Box bg="gray.50" p={4} borderRadius="md" mt={2}>
                      <Text fontSize="sm" color="gray.600" mb={2}>Preview:</Text>
                      <HStack align="baseline" spacing={2}>
                        <Text fontSize="xl" fontWeight="bold" color="green.600">
                          ${pricing.proMonthlyPrice}/month
                        </Text>
                        <Text fontSize="sm" textDecoration="line-through" color="gray.400">
                          ${pricing.proOriginalPrice}
                        </Text>
                        <Badge colorScheme="green">{pricing.proDiscountPercent}% off launch pricing</Badge>
                      </HStack>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>

            <TabPanel px={0}>
              <VStack spacing={4} align="stretch">
                <HStack justify="flex-end">
                  <FormControl display="flex" alignItems="center" w="auto">
                    <FormLabel htmlFor="hide-admins" mb="0" fontSize="sm" color="gray.600">
                      Hide admin users
                    </FormLabel>
                    <Switch
                      id="hide-admins"
                      isChecked={hideAdminUsers}
                      onChange={(e) => setHideAdminUsers(e.target.checked)}
                      colorScheme="purple"
                    />
                  </FormControl>
                </HStack>
                {(() => {
                  const filteredIntents = hideAdminUsers 
                    ? upgradeIntents.filter(i => !i.is_admin) 
                    : upgradeIntents;
                  const totalIntents = new Set(filteredIntents.map(i => i.user_id)).size;
                  const conversions = filteredIntents.filter(i => i.converted_at);
                  const totalConversions = new Set(conversions.map(i => i.user_id)).size;
                  const pendingIntents = totalIntents - totalConversions;
                  const conversionRate = totalIntents > 0 ? Math.round((totalConversions / totalIntents) * 100) : 0;
                  
                  return (
                    <HStack spacing={4}>
                      <Card flex={1}>
                        <CardBody py={4}>
                          <Text fontSize="sm" color="gray.500">Total Intents</Text>
                          <Text fontSize="2xl" fontWeight="bold" color="purple.600">{totalIntents}</Text>
                          <Text fontSize="xs" color="gray.400">Clicked "Join Pro"</Text>
                        </CardBody>
                      </Card>
                      <Card flex={1}>
                        <CardBody py={4}>
                          <Text fontSize="sm" color="gray.500">Conversions</Text>
                          <Text fontSize="2xl" fontWeight="bold" color="green.600">{totalConversions}</Text>
                          <Text fontSize="xs" color="gray.400">Confirmed upgrade</Text>
                        </CardBody>
                      </Card>
                      <Card flex={1}>
                        <CardBody py={4}>
                          <Text fontSize="sm" color="gray.500">Pending</Text>
                          <Text fontSize="2xl" fontWeight="bold" color="orange.500">{pendingIntents}</Text>
                          <Text fontSize="xs" color="gray.400">Intent only</Text>
                        </CardBody>
                      </Card>
                      <Card flex={1}>
                        <CardBody py={4}>
                          <Text fontSize="sm" color="gray.500">Conversion Rate</Text>
                          <Text fontSize="2xl" fontWeight="bold" color="blue.600">{conversionRate}%</Text>
                          <Text fontSize="xs" color="gray.400">Intent → Pro</Text>
                        </CardBody>
                      </Card>
                    </HStack>
                  );
                })()}

                <Card>
                  <CardHeader>
                    <Heading size="md">Upgrade Funnel</Heading>
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Track intent (clicked Join Pro) vs conversion (confirmed upgrade)
                    </Text>
                  </CardHeader>
                  <CardBody>
                    {(() => {
                      const filteredIntents = hideAdminUsers 
                        ? upgradeIntents.filter(i => !i.is_admin) 
                        : upgradeIntents;
                      const groupedByUser = filteredIntents.reduce((acc, intent) => {
                        const existing = acc[intent.user_id];
                        if (!existing || new Date(intent.created_at) > new Date(existing.latestIntent.created_at)) {
                          acc[intent.user_id] = {
                            latestIntent: intent,
                            totalIntents: (existing?.totalIntents || 0) + 1,
                            hasConverted: intent.converted_at || existing?.hasConverted,
                            previousIntents: existing ? [...(existing.previousIntents || []), existing.latestIntent] : []
                          };
                        } else {
                          acc[intent.user_id].totalIntents += 1;
                          acc[intent.user_id].previousIntents.push(intent);
                          if (intent.converted_at) acc[intent.user_id].hasConverted = true;
                        }
                        return acc;
                      }, {});
                      
                      const groupedIntents = Object.values(groupedByUser);
                      
                      return groupedIntents.length === 0 ? (
                        <Text color="gray.500" textAlign="center" py={8}>
                          No upgrade intents yet
                        </Text>
                      ) : (
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>User</Th>
                              <Th>Email</Th>
                              <Th>Funnel Stage</Th>
                              <Th>Trigger</Th>
                              <Th>Intent Date</Th>
                              <Th>Action</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {groupedIntents.map(({ latestIntent: intent, totalIntents, hasConverted }) => (
                              <Tr key={intent.id}>
                                <Td>
                                  <HStack spacing={2}>
                                    <Text>{intent.first_name} {intent.last_name}</Text>
                                    {totalIntents > 1 && (
                                      <Badge colorScheme="purple" fontSize="xs" borderRadius="full">
                                        +{totalIntents - 1} previous
                                      </Badge>
                                    )}
                                  </HStack>
                                </Td>
                                <Td>{intent.email}</Td>
                                <Td>
                                  {hasConverted || intent.converted_at ? (
                                    <Badge colorScheme="green" fontSize="xs">
                                      Converted
                                    </Badge>
                                  ) : (
                                    <Badge colorScheme="orange" fontSize="xs">
                                      Intent Only
                                    </Badge>
                                  )}
                                </Td>
                                <Td>
                                  <Badge colorScheme="gray" fontSize="xs">
                                    {intent.trigger_context || "manual"}
                                  </Badge>
                                </Td>
                                <Td fontSize="sm" color="gray.500">
                                  {formatDate(intent.created_at)}
                                </Td>
                                <Td>
                                  {intent.plan_type === 'pro' ? (
                                    <Text fontSize="xs" color="green.600">Pro User</Text>
                                  ) : (
                                    <Button
                                      size="xs"
                                      colorScheme="brand"
                                      onClick={() => handleSwitchToPro(intent.user_id, `${intent.first_name} ${intent.last_name}`)}
                                      isLoading={switchingUser === intent.user_id}
                                      loadingText="Switching..."
                                    >
                                      Manual Upgrade
                                    </Button>
                                  )}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      );
                    })()}
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};

export default AdminPlanSettings;
